import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { SITE, absoluteUrl } from '../config/site.js';

export function Seo({ title, description, path = '/' }) {
  const fullTitle = title.includes('Unlistr') ? title : `${title} — Unlistr`;
  const url = absoluteUrl(path);
  return <Helmet>
    <title>{fullTitle}</title>
    <meta name="description" content={description} />
    <link rel="canonical" href={url} />
    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="Unlistr" />
    <meta property="og:title" content={fullTitle} />
    <meta property="og:description" content={description} />
    <meta property="og:url" content={url} />
    <meta name="twitter:card" content="summary" />
    <meta name="twitter:title" content={fullTitle} />
    <meta name="twitter:description" content={description} />
  </Helmet>;
}

export function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname, location.hash]);
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [open]);
  return <header className="site-header">
    <div className="container header-inner">
      <div className="header-brand-lockup">
        <Link className="brand" to="/" aria-label="Unlistr home">
          <img src="/assets/unlistr-logo.png" alt="" /><span>Unlistr</span>
        </Link>
        <span className="header-brand-for">for</span>
        <a className="header-shopify-brand" href="https://www.shopify.com/" target="_blank" rel="noreferrer" aria-label="Shopify website">
          <img src="/assets/shopify-logo.svg" alt="Shopify" />
        </a>
      </div>
      <button className="menu-button" type="button" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="site-nav" onClick={() => setOpen(!open)}>
        {open ? <X /> : <Menu />}
      </button>
      <nav id="site-nav" className={open ? 'nav open' : 'nav'} aria-label="Primary navigation">
        <Link to="/#how-it-works">How it works</Link>
        <Link to="/#features">Features</Link>
        <NavLink to="/pricing">Pricing</NavLink>
        <Link to="/#faq">FAQ</Link>
        <a className="button button-small" href={SITE.appUrl} target="_blank" rel="noreferrer">Start free <ArrowUpRight size={16} /></a>
      </nav>
    </div>
  </header>;
}

export function Footer() {
  return <footer className="footer">
    <div className="container footer-grid">
      <div><Link className="brand brand-light" to="/"><img src="/assets/unlistr-logo.png" alt="" /><span>Unlistr</span></Link><p>Keep your storefront clean. Keep product URLs live.</p></div>
      <div><h2>Product</h2><Link to="/#how-it-works">How it works</Link><Link to="/#features">Features</Link><Link to="/pricing">Pricing</Link></div>
      <div><h2>Legal</h2><Link to="/legal/privacy">Privacy policy</Link><Link to="/legal/terms">Terms of service</Link></div>
    </div>
    <div className="container footer-bottom"><span>© {new Date().getFullYear()} {SITE.company}</span><span>Built by <a href="https://ramiapps.com" target="_blank" rel="noreferrer">Rami Apps</a></span></div>
  </footer>;
}

export function Layout({ children }) {
  return <><Header /><main>{children}</main><Footer /></>;
}

export function SectionHeading({ eyebrow, title, copy, light = false, centered = false }) {
  return <div className={`section-heading${light ? ' light' : ''}${centered ? ' centered' : ''}`}>
    <p className="eyebrow">{eyebrow}</p><h2>{title}</h2>{copy && <p className="section-copy">{copy}</p>}
  </div>;
}

export function StartButton({ children = 'Start free', className = '' }) {
  return <a className={`button ${className}`} href={SITE.appUrl} target="_blank" rel="noreferrer">{children} <ArrowUpRight size={18} /></a>;
}

export function FinalCta() {
  return <section className="final-cta"><div className="container final-cta-inner"><div><p className="eyebrow">Ready when you are</p><h2>Keep sold-out products out of the way—not out of your catalog.</h2><p>Let Unlistr handle repetitive status changes while you control what gets managed and when it returns.</p></div><div className="cta-actions"><StartButton /><Link className="text-link light-link" to="/#how-it-works">See how it works →</Link></div></div></section>;
}
